export const salesAffiliatePaymentSetupGuide = {
  title: "Sales Affiliate Payment Setup Guide",
  subtitle: "Getting Ready to Receive Eligible Commission Payments",
  providerLabel: "Payment Account Provider",
  currentProvider: "Stripe",
  activationClarification:
    "Payment Setup is not required to activate your AI Fine Protector Sales Affiliate account or to begin signing up Prospects and Customers. However, you must complete Payment Setup before AFP can send eligible commission payments to you.",
  introduction:
    "AI Fine Protector uses a third-party Payment Account Provider — currently Stripe — to collect the information needed to establish your payment account. Stripe, rather than AFP, determines what identity, business, banking, or regulatory information it needs from you, so the exact screens you see may vary.",
  sections: [
    {
      id: "start",
      title: "1. Start Payment Setup in AFP",
      paragraphs: [
        "Sign in to your AI Fine Protector Sales Affiliate account and go to the Commission Payment Setup section.",
        "If Payment Setup has not been completed, AFP will provide an available action such as Start Payment Setup. Select it to continue.",
        "AFP will securely send you to Stripe's hosted Payment Setup process.",
      ],
      subheading: "Before you continue",
      bullets: [
        "your legal name",
        "current address and contact information",
        "your actual business structure",
        "business or tax-identification information if Stripe requests it",
        "identity-verification information",
        "the bank account you want to use for commission payments",
      ],
      helpers: [
        {
          label: "Helpful hint",
          tone: "blue",
          text: "Stripe may not ask every Sales Affiliate for exactly the same information. Follow the screens that are presented to you rather than worrying if your process looks slightly different from an example.",
        },
      ],
    },
    {
      id: "business",
      title: "2. Tell Stripe About You or Your Business",
      paragraphs: [
        "Stripe may ask what type of business or activity you operate.",
        "Choose the option that truthfully describes your actual situation.",
        "Depending on your circumstances, Stripe may distinguish among options such as an individual, sole proprietorship or unregistered business, or registered business or company.",
      ],
      subheading: "What should I select?",
      footer:
        "Use your real legal or business status. Do not select an option merely because it appeared in an AFP example or test.",
      helpers: [
        {
          label: "Helpful hint",
          tone: "blue",
          text: "If you operate in your own name and have not formed a separate legal business entity, Stripe may present an individual, sole-proprietor, or unregistered-business type of option. Select only the description that accurately fits you.",
        },
        {
          label: "Important",
          tone: "amber",
          text: "If you are uncertain about your legal business structure, do not guess simply to move through the screen.",
        },
      ],
    },
    {
      id: "ein",
      title: "3. EIN or Other Business Identification",
      paragraphs: [
        "Depending on the information you provide, Stripe may ask whether you have an Employer Identification Number (EIN) or request other identifying information.",
        "Answer according to your actual circumstances. Some Sales Affiliates may have an EIN. Others may not.",
      ],
      helpers: [
        {
          label: "Helpful hint",
          tone: "blue",
          text: "A test account may have followed a “No EIN” path, but that does not mean you should choose “No” if you actually have an EIN.",
        },
      ],
      subheading: "AFP and Stripe requirements are different",
      footer:
        "AFP currently does not require Payment Setup as part of Sales Affiliate activation. Stripe may nevertheless ask for information it independently needs for identity, banking, regulatory, or payment purposes.",
      trailingHelpers: [
        {
          label: "What this means",
          tone: "slate",
          text: "A question appearing inside Stripe does not necessarily mean AFP has added a new onboarding requirement. It may simply be part of Stripe's own payment-account verification process.",
        },
      ],
    },
    {
      id: "identity",
      title: "4. Enter Your Personal and Identity Information",
      paragraphs: [
        "Stripe may ask for information needed to identify and verify the person associated with the payment account.",
        "Follow the instructions displayed by Stripe and provide accurate information.",
        "Depending on your circumstances, Stripe may ask for additional verification.",
      ],
      helpers: [
        {
          label: "You're still on track",
          tone: "emerald",
          text: "Stripe may request more verification from some people than from others. That does not necessarily mean there is a problem with your AFP Sales Affiliate account.",
        },
      ],
      subheading: "Protecting sensitive information",
      footer:
        "Where practical, sensitive identity and banking information is handled by the Payment Account Provider rather than collected directly by AFP.",
      trailingHelpers: [
        {
          label: "Helpful hint",
          tone: "blue",
          text: "Always confirm that you are entering sensitive information on the Stripe-hosted Payment Setup screen before submitting it.",
        },
      ],
    },
    {
      id: "bank",
      title: "5. Connect the Bank Account for Commission Payments",
      paragraphs: [
        "Stripe will guide you through connecting the bank account you intend to use for commission payments.",
        "Follow the Stripe instructions to select or connect your account.",
        "Depending on the options Stripe presents, you may be able to securely connect through your financial institution or use another bank-account verification method.",
        "Continue until Stripe confirms that the banking step has been completed.",
      ],
      helpers: [
        {
          label: "Helpful hint",
          tone: "blue",
          text: "Use the bank account where you actually want eligible AFP commission payments to be sent.",
        },
        {
          label: "What happens next",
          tone: "slate",
          text: "After your banking information is connected, Stripe may move you directly to review or may first request additional identity or account information.",
        },
      ],
    },
    {
      id: "link",
      title: "6. “Save Account with Link” or “Finish Without Saving”",
      paragraphs: [
        "Stripe may offer an option to save your information using Link.",
        "You may see choices similar to Save account with Link or Finish without saving.",
      ],
      subheading: "What is Link?",
      footer:
        "Link is a Stripe service that can save certain information for convenience when you use Stripe-supported services in the future.",
      trailingParagraphs: [
        "Is Link required for AFP? No.",
        "You do not have to save your information with Link to complete AFP Payment Setup. If you do not want to use Link, you may choose Finish without saving and continue.",
      ],
      trailingHelpers: [
        {
          label: "Helpful hint",
          tone: "blue",
          text: "This is a convenience choice, not an AFP requirement. Choose whichever option you prefer.",
        },
      ],
    },
    {
      id: "verification",
      title: "7. Complete Any Additional Verification Stripe Requires",
      paragraphs: [
        "Stripe may request additional information or identity verification before it can complete your payment account.",
        "Follow the instructions displayed on the Stripe screen.",
        "The type of verification may vary based on the information you provided.",
      ],
      helpers: [
        {
          label: "You're still on track",
          tone: "emerald",
          text: "Seeing an additional verification request does not necessarily mean you entered something incorrectly. Stripe sometimes requires additional information before completing a payment account.",
        },
      ],
      footer:
        "If Stripe identifies something that needs attention, follow the instructions it provides and complete the requested step.",
    },
    {
      id: "review",
      title: "8. Review Your Information",
      paragraphs: [
        "After the necessary information has been entered, Stripe may show a Review and Submit screen.",
        "Review the information carefully and correct anything that is inaccurate before submitting.",
        "When you are satisfied that the information is correct, use the submission button displayed by Stripe. Its wording may appear as something such as Agree and submit.",
        "Stripe may change its wording over time, so follow the action shown on your screen.",
      ],
      helpers: [
        {
          label: "Helpful hint",
          tone: "blue",
          text: "Take an extra moment here. Correcting inaccurate information before submission is usually easier than trying to resolve it later.",
        },
      ],
    },
    {
      id: "return",
      title: "9. Return to AI Fine Protector",
      paragraphs: [
        "After you submit the Stripe process, you should be returned to AI Fine Protector.",
        "There may be a brief redirect while that happens.",
      ],
      helpers: [
        {
          label: "What happens next",
          tone: "slate",
          text: "AFP will check the status returned by the Payment Account Provider and display the appropriate Payment Setup status in your Sales Affiliate account.",
        },
      ],
      footer:
        "Do not close the process prematurely if Stripe appears to be returning you to AFP.",
    },
    {
      id: "status",
      title: "10. Check Your Payment Setup Status in AFP",
      paragraphs: [
        "After returning to AFP, review the Commission Payment Setup section.",
        "When the necessary requirements have been completed, AFP should indicate that your Payment Setup has been verified or completed.",
      ],
      subheading: "If AFP says additional action is required",
      footer:
        "Use the Payment Setup action provided by AFP to return to Stripe and complete whatever remains outstanding.",
      helpers: [
        {
          label: "Helpful hint",
          tone: "blue",
          text: "Payment Setup being non-blocking for account activation does not mean it is already complete. Look at the actual Payment Setup status shown in AFP.",
        },
      ],
      trailingSubheading: "If your setup is verified",
      trailingParagraphs: [
        "No additional Payment Setup action should normally be required unless Stripe later requests updated information.",
      ],
      trailingHelpers: [
        {
          label: "You're all set",
          tone: "emerald",
          text: "Once AFP shows that Payment Setup is verified, the payment-account portion of your Sales Affiliate setup is complete.",
        },
      ],
    },
    {
      id: "separate",
      title: "11. Payment Setup Is Separate From Account Activation",
      paragraphs: ["This distinction is important."],
      subheading: "Sales Affiliate account activation",
      footer:
        "Payment Setup is not required to activate your AFP Sales Affiliate account.",
      trailingSubheading: "Receiving eligible commission payments",
      trailingParagraphs: [
        "Payment Setup must be completed before AFP can send eligible commission payments to you.",
        "That means you may become an active Sales Affiliate before Payment Setup has been completed.",
      ],
      trailingHelpers: [
        {
          label: "Helpful hint",
          tone: "blue",
          text: "Completing Payment Setup sooner rather than later can help prevent an avoidable payment delay when a commission later becomes eligible to be sent.",
        },
      ],
    },
    {
      id: "help",
      title: "If You Need Help",
      paragraphs: [
        "If you are unsure about an AFP navigation step, return to your Sales Affiliate account and review the Commission Payment Setup section and the available AFP guidance.",
        "If your question concerns information Stripe itself is requesting, follow the instructions presented by Stripe.",
        "AFP should not guess at or override a requirement imposed by the Payment Account Provider.",
      ],
      bullets: [
        "identity verification",
        "business classification",
        "banking information",
        "additional verification requirements",
      ],
      helpers: [
        {
          label: "Quick rule",
          tone: "slate",
          text: "AFP question? Use AFP guidance. Stripe-required information? Follow the Stripe screen.",
        },
      ],
    },
  ],
};

export const paymentSetupQuickGuide = {
  title: "What to expect from the Payment Account Provider",
  bullets: [
    "Have your legal name, contact information, business details (if applicable), identification information, and commission-payment bank information ready.",
    "Answer business or entity questions based on how you actually operate. AFP cannot provide legal, tax, or accounting classification advice.",
    "The Payment Account Provider may request identity, business, banking, or additional verification information.",
    "If the Payment Account Provider offers Save account with Link, it is optional. Finish without saving is also acceptable for AFP Payment Setup.",
    "If additional information is required, return through AFP's Payment Setup section and follow the available action.",
  ],
};
